import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormHelperText from '@mui/material/FormHelperText';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';

import { useResponsive } from 'src/hooks/use-responsive';

import { DateRangePickerProps } from './types';

// ----------------------------------------------------------------------

export default function CustomDateRangePicker({
  title = 'Select date range',
  variant = 'input',
  //
  startDate,
  endDate,
  //
  onChangeStartDate,
  onChangeEndDate,
  //
  open,
  onClose,
  onApply,
  onReset,
  //
  error,
  disablePast = false,
  startLabel = 'Start date',
  endLabel = 'End date',
  resetLabel = 'Clear',
  cancelLabel = 'Cancel',
  applyLabel = 'Apply',
  errorMessage = 'End date must be later than start date',
}: DateRangePickerProps) {
  const mdUp = useResponsive('up', 'md');

  const isCalendarView = variant === 'calendar';

  return (
    <Dialog
      fullWidth
      maxWidth={isCalendarView ? false : 'xs'}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          ...(isCalendarView && {
            maxWidth: 720,
          }),
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>{title}</DialogTitle>

      <DialogContent
        sx={{
          ...(isCalendarView &&
            mdUp && {
              overflow: 'unset',
            }),
        }}
      >
        <Stack
          justifyContent="center"
          spacing={isCalendarView ? 3 : 2}
          direction={isCalendarView && mdUp ? 'row' : 'column'}
          sx={{ pt: 1 }}
        >
          {isCalendarView ? (
            <>
              <Stack spacing={1}>
                <Typography variant="subtitle2">{startLabel}</Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    borderColor: 'divider',
                    borderStyle: 'dashed',
                  }}
                >
                  <DateCalendar
                    value={startDate}
                    onChange={onChangeStartDate}
                    disablePast={disablePast}
                  />
                </Paper>
              </Stack>

              <Stack spacing={1}>
                <Typography variant="subtitle2">{endLabel}</Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    borderColor: 'divider',
                    borderStyle: 'dashed',
                  }}
                >
                  <DateCalendar
                    value={endDate}
                    onChange={onChangeEndDate}
                    disablePast={disablePast}
                    minDate={startDate ?? undefined}
                  />
                </Paper>
              </Stack>
            </>
          ) : (
            <>
              <DatePicker
                label={startLabel}
                value={startDate}
                onChange={onChangeStartDate}
                disablePast={disablePast}
              />

              <DatePicker
                label={endLabel}
                value={endDate}
                onChange={onChangeEndDate}
                disablePast={disablePast}
                minDate={startDate ?? undefined}
              />
            </>
          )}
        </Stack>

        {error && (
          <FormHelperText error sx={{ px: 2 }}>
            {errorMessage}
          </FormHelperText>
        )}
      </DialogContent>

      <DialogActions>
        {onReset && (
          <Button color="inherit" onClick={onReset} sx={{ mr: 'auto' }}>
            {resetLabel}
          </Button>
        )}

        <Button variant="outlined" color="inherit" onClick={onClose}>
          {cancelLabel}
        </Button>

        <Button disabled={error} variant="contained" onClick={onApply ?? onClose}>
          {applyLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
